import React, {
  ChangeEvent,
  FC,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import cls from './Select.module.scss';
import { ClickAwayListener, Popper } from '@mui/material';
import { Option, OptionValue, SelectProps, SelectThemes } from './types';
import useRecursiveSelect from './useRecursiveSelect';
import { useTranslation } from 'react-i18next';
import SelectController from './SelectController';
import Dropdown from './Dropdown';

const Select: FC<SelectProps> = ({
  className,
  options,
  hasSearch = false,
  recursiveSelect,
  propsValue,
  defaultValue,
  noValueText,
  disabled = false,
  onSearch,
  controllerClassName,
  isMultiSelect = false,
  theme = SelectThemes.TRANSPARENT,
  register,
  setValueInForm,
  onChange,
  fetchNextPage,
  hasMore = false,
  disableSearchLimit = false,
  errorText,
  disablePortal = true,
  container,
  isOptionsCenter = false,
  withOptionsDivider = true,
  dropdownClassName = '',
  onOpen,
  dataQaSelector,
  iconClassname = '',
  approveNullValue,
}) => {
  const { t: tg } = useTranslation('translation');
  const selectRef = useRef<HTMLDivElement>(null);
  const [hoveredElementKey, setHoveredElementKey] = useState<
    string | number | undefined | null
  >(null);
  const [searchValue, setSearchValue] = useState<string>('');
  const [selectedValue, setSelectedValue] = useState<OptionValue>();
  const [selectedValues, setSelectedValues] = useState<OptionValue[]>([]);

  //save option for async select
  const [selectedOptionsAsync, setSelectedOptionsAsync] = useState<Option[]>(
    []
  );
  const [selectedOptionAsync, setSelectedOptionAsync] = useState<Option>();

  //recursive select hook
  const recursiveSelectReturned = useRecursiveSelect({
    recursiveSelect,
    propsValue,
  });
  // dropdown control
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const handleClick = (event: React.MouseEvent<any>) => {
    if (disabled) return;
    onOpen?.();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (event?: MouseEvent | TouchEvent) => {
    //@ts-ignore
    if (anchorEl && !event?.target?.dataset?.noclose) {
      setAnchorEl(null);
    }
  };
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popper' : undefined;
  const isSelectedAll = selectedValues.length === options?.length;

  // options tranformation & options filter
  const mainOptions = useMemo(() => {
    let res: Option[] = [];
    if (
      recursiveSelect &&
      recursiveSelectReturned.recursiveOptions &&
      !disabled
    ) {
      res = recursiveSelectReturned.recursiveOptions;
    } else {
      res = options ? [...options] : [];
    }

    // Додаємо noValue у всіх випадках, якщо потрібно
    if ((!res[0] || res[0].value !== null) && !defaultValue) {
      res.unshift({
        title: noValueText || tg('select-no-value'),
        value: null,
      });
    }
    return res;
  }, [
    recursiveSelectReturned,
    options,
    propsValue,
    noValueText,
    searchValue,
    onSearch,
  ]);
  const filteredOptions = useMemo(() => {
    //filter options if search not async
    let res: Option[] = [...mainOptions];
    if (searchValue && !onSearch) {
      res = mainOptions.filter((item) =>
        (item.searchValue || item.title || '')
          .toLowerCase()
          .includes(searchValue.toLocaleLowerCase())
      );
    }
    if (isMultiSelect) {
      res = res.filter(
        (item) => !selectedValues.includes(item.value) && item.value !== null
      );

      res.unshift({
        title: isSelectedAll ? 'Прибрати всі' : 'Обрати всі',
        value: '_select_all_',
      });
    }
    return res;
  }, [mainOptions, searchValue, onSearch, selectedValues]);
  //async search
  const searchTimeout = useRef<any>(null);
  const onSearchHandler = (event: ChangeEvent<HTMLInputElement>) => {
    if (hasSearch) {
      const value = event.target.value;
      setSearchValue(value);
      if (
        onSearch &&
        (disableSearchLimit || value.length > 2 || !value.length)
      ) {
        if (onSearch) {
          clearTimeout(searchTimeout.current);
          searchTimeout.current = setTimeout(() => {
            onSearch(value);
          }, 500);
        }
      }
    }
  };
  //select value
  const selectValue = (value: OptionValue, event?: React.MouseEvent) => {
    if (value === '_select_all_') {
      const values = isSelectedAll
        ? []
        : mainOptions.map((option) => option.value).filter(Boolean);
      setSelectedValues(values);

      if (onChange) {
        onChange(values, event);
      }
      if (setValueInForm && register) {
        setValueInForm(register.name, values);
      }
      handleClose();

      return;
    }

    // Інакше логіка звичайного селекту
    let res: OptionValue | OptionValue[] = value;
    if (isMultiSelect) {
      res = [...selectedValues, value];
      setSelectedValues(res);

      if (onSearch && res) {
        const selectedOptionsRes: Option[] = [];
        [...selectedOptionsAsync, ...mainOptions].forEach((item) => {
          if (!res) return;
          if (
            !selectedOptionsRes.find((option) => option.value == item.value) &&
            //@ts-ignore
            res.find((optionValue) => optionValue == item.value)
          ) {
            selectedOptionsRes.push(item);
          }
        });
        setSelectedOptionsAsync(selectedOptionsRes);
      }
    } else {
      if ((setValueInForm && register?.name) || onChange) {
        res = defaultValue ? value || defaultValue : value;
        setSelectedValue(res);
        if (onSearch) {
          setSelectedOptionAsync(mainOptions.find((item) => item.value == res));
        }
      }
      handleClose();
    }
    if (onChange && (res || res === null) && propsValue !== value) {
      onChange(res, event);
    }
    if (setValueInForm && register) {
      setValueInForm(register?.name, res, { shouldValidate: !!event });
    }
  };
  const multiselectRemoveHandler = (
    value: OptionValue,
    event?: React.MouseEvent
  ) => {
    if (isMultiSelect) {
      event?.stopPropagation();
      const res = selectedValues.filter((item) => item != value);
      setSelectedValues(res);
      handleClose();
      if (onChange) {
        onChange(res, event);
      }
      if (setValueInForm && register) {
        setValueInForm(register?.name, res);
      }
    }
  };
  useEffect(() => {
    if (isMultiSelect || Array.isArray(propsValue)) {
      if (
        Array.isArray(propsValue) &&
        JSON.stringify(propsValue) !== JSON.stringify(selectedValues)
      ) {
        setSelectedValues(propsValue);
      }
      return;
    }
    if (propsValue != selectedValue) {
      selectValue(propsValue);
    }
  }, [propsValue, options, selectedValue]);
  useEffect(() => {
    if (defaultValue && !propsValue && defaultValue != propsValue) {
      selectValue(defaultValue);
    }
  }, [defaultValue, propsValue]);

  return (
    <div
      className={`${cls.select} ${cls[theme]} ${open && cls['select--open']} ${isMultiSelect && cls.multiselect} ${!withOptionsDivider && cls['disable-options-line']} ${className}`}
      ref={selectRef}
    >
      <div
        className={cls['select-inner-div']}
        aria-describedby={id}
        onClick={handleClick}
        data-qa-selector={dataQaSelector}
      >
        <SelectController
          theme={theme}
          isMultiSelect={isMultiSelect}
          selectedOptionAsync={selectedOptionAsync}
          controllerClassName={controllerClassName}
          selectedOptionsAsync={selectedOptionsAsync}
          selectedValues={selectedValues}
          mainOptions={mainOptions}
          selectedValue={selectedValue}
          hasSearch={hasSearch}
          open={open}
          searchValue={searchValue}
          onSearchHandler={onSearchHandler}
          multiselectRemoveHandler={multiselectRemoveHandler}
          iconClassname={iconClassname}
          approveNullValue={approveNullValue}
        />
        {errorText && (
          <strong style={{ fontWeight: '400', marginLeft: '10px' }}>
            {errorText as string}
          </strong>
        )}
      </div>

      {open && (
        <ClickAwayListener onClickAway={handleClose}>
          <Popper
            id={id}
            open={open}
            anchorEl={anchorEl}
            placement="bottom-start"
            disablePortal={disablePortal}
            container={
              disablePortal ? selectRef.current : container || document?.body
            }
            slotProps={{
              root: {
                className: `${cls[theme]} ${cls.popper} select-popper ${theme}`,
              },
            }}
          >
            <Dropdown
              hoveredElementKey={hoveredElementKey}
              className={`${dropdownClassName} ${isOptionsCenter && cls['options-center']}`}
              selectValue={selectValue}
              options={filteredOptions}
              width={selectRef.current?.offsetWidth}
              hasMore={hasMore}
              fetchNextPage={fetchNextPage}
              setHoveredElementKey={setHoveredElementKey}
            />
          </Popper>
        </ClickAwayListener>
      )}
    </div>
  );
};

export default Select;
